import { useGetActiveIngredients, useGetCultures } from '@practium/features/api';
import type { FormSelectOption } from '@practium/ui';
import { FormField, FormInput, FormSectionTitle, FormSelect, RowActionButton } from '@practium/ui';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import {
	APPLICATION_METHOD_OPTIONS,
	DEFENSIVE_CLASS_OPTIONS,
	LEGACY_DEFENSIVE_APPLICATION_METHOD_LABELS,
	NATURE_OPTIONS,
} from '../../../constants/defensive.constants';
import { useDefensiveForm } from '../../../hooks/useDefensiveForm';
import type { DefensiveFormData, Manufacturer, ProductPageMode } from '../../../types';
import { NutrientInput } from '../../NutrientInput/NutrientInput';
import generalStyles from '../../ProductFormCard/ProductFormCard.module.css';
import { AddActiveIngredientModal } from '../ActiveIngredientForm/AddActiveIngredientModal';
import { AddCultureModal } from '../CultureForm/AddCultureModal';
import { AddManufacturerModal } from '../ManufacturerForm/AddManufacturerModal';
import styles from './DefensiveForm.module.css';

const ADD_NEW_MANUFACTURER = '__add_new__';
const ADD_NEW_CULTURE = '__add_new_culture__';
const ADD_NEW_ACTIVE_INGREDIENT = '__add_new_active_ingredient__';

export interface DefensiveFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

export interface DefensiveFormProps {
	manufacturers: Manufacturer[];
	mode?: ProductPageMode;
	initialData?: DefensiveFormData;
	onSubmit: () => void;
	onLoadingChange?: (loading: boolean) => void;
	onIdentifierChange?: (name: string, nature: string, manufacturer: string, extra: string) => void;
	id?: string;
	file?: File[];
}

const resolveLabel = (options: FormSelectOption[], value: string) =>
	(options.find((o) => o.value === value)?.label ?? value) || '—';

const resolveApplicationMethodLabels = (values: string[]) =>
	values.length > 0
		? values
			.map(
				(value) =>
					APPLICATION_METHOD_OPTIONS.find((option) => option.value === value)?.label ??
					LEGACY_DEFENSIVE_APPLICATION_METHOD_LABELS[value] ??
					value
			)
			.join(', ')
		: '—';

export const DefensiveForm = forwardRef<DefensiveFormRef, DefensiveFormProps>(
	function DefensiveForm(
		{ manufacturers, mode = 'add', initialData, onSubmit, onLoadingChange, onIdentifierChange, id, file },
		ref
	) {
		const [showAddManufacturerModal, setShowAddManufacturerModal] = useState(false);
		const [showAddCultureModal, setShowAddCultureModal] = useState(false);
		const [showAddActiveIngredientModal, setShowAddActiveIngredientModal] = useState(false);
		const [activeIngredientRowIndex, setActiveIngredientRowIndex] = useState<number | null>(null);

		const {
			form,
			ingredientKeys,
			errors,
			compositionErrors,
			isLoading,
			set,
			setApplicationForm,
			classificationRows,
			updateClassificationRow,
			addClassificationRow,
			removeClassificationRow,
			updateIngredient,
			addIngredient,
			removeIngredient,
			handleSubmit,
			isDirty,
		} = useDefensiveForm({ manufacturers, initialData, onSubmit, id, file });

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));
		const getManufacturerName = useCallback(
			(id: string) => {
				const m = manufacturers.find((item) => item._id === id);
				return m ? (m.tradingName ?? m.businessName ?? '') : '';
			},
			[manufacturers]
		);

		const broadcastChanges = useCallback(
			(data: DefensiveFormData) => {
				onIdentifierChange?.(
					data.commercialName,
					data.nature,
					getManufacturerName(data.manufacturerId),
					data.class.map(item => item.class).join(', ')
				);
			},
			[onIdentifierChange, getManufacturerName]
		);

		useEffect(() => {
			if (mode === 'view' && initialData) {
				broadcastChanges(initialData);
			}
		}, [mode, initialData, broadcastChanges]);


		useEffect(() => {
			onLoadingChange?.(isLoading);
		}, [isLoading, onLoadingChange]);

		const isView = mode === 'view';
		const concentrationUnit = form.nature === 'solid' ? '%' : 'g/L';
		const isExclusiveFertigation =
			form.applicationForm.length === 1 && form.applicationForm[0] === 'irrigation';
		const selectableApplicationForms = form.applicationForm.filter((value) => value !== 'broad');

		const manufacturerOptions: FormSelectOption[] = [
			{ value: ADD_NEW_MANUFACTURER, label: '+ Adicionar novo fabricante', action: true },
			...manufacturers.map((m) => ({
				value: m._id,
				label: m.tradingName ?? m.businessName ?? '',
			})),
		];

		const triggerChange = (updatedForm: DefensiveFormData) => {
			onIdentifierChange?.(
				updatedForm.commercialName,
				updatedForm.nature,
				resolveLabel(manufacturerOptions, updatedForm.manufacturerId),
				updatedForm.class.map(item => item.class).join(', ')
			);
		};

		const handleManufacturerChange = (v: string) => {
			if (v === ADD_NEW_MANUFACTURER) {
				setShowAddManufacturerModal(true);
				return;
			}
			set('manufacturerId', v);
			triggerChange({ ...form, manufacturerId: v });
		};

		const handleManufacturerCreated = (newId: string) => {
			setShowAddManufacturerModal(false);
			set('manufacturerId', newId);
			triggerChange({ ...form, manufacturerId: newId });
		};

		const { data: cultures } = useGetCultures({ enabled: !isView });

		const cultureOptions: FormSelectOption[] = [
			{ value: ADD_NEW_CULTURE, label: '+ Adicionar nova cultura', action: true },
			...[...new Set([...cultures.map((c) => c.name), ...form.culture])]
				.filter(Boolean)
				.sort((a, b) => a.localeCompare(b, 'pt-BR'))
				.map((name) => ({ value: name, label: name })),
		];

		const handleCultureChange = (values: string[]) => {
			if (values.includes(ADD_NEW_CULTURE)) {
				setShowAddCultureModal(true);
				set('culture', values.filter((v) => v !== ADD_NEW_CULTURE));
				return;
			}
			set('culture', values);
		};

		const handleCultureCreated = (name: string) => {
			setShowAddCultureModal(false);
			if (!form.culture.includes(name)) set('culture', [...form.culture, name]);
		};

		const { data: activeIngredientsCatalog } = useGetActiveIngredients({ enabled: !isView });

		const activeIngredientOptions: FormSelectOption[] = [
			{ value: ADD_NEW_ACTIVE_INGREDIENT, label: '+ Adicionar novo ingrediente', action: true },
			...[
				...new Set([
					...activeIngredientsCatalog.map((ai) => ai.name),
					...form.activeIngredients.map((row) => row.name),
				]),
			]
				.filter(Boolean)
				.sort((a, b) => a.localeCompare(b, 'pt-BR'))
				.map((name) => ({ value: name, label: name })),
		];

		const handleIngredientNameChange = (index: number, value: string) => {
			if (value === ADD_NEW_ACTIVE_INGREDIENT) {
				setActiveIngredientRowIndex(index);
				setShowAddActiveIngredientModal(true);
				return;
			}
			updateIngredient(index, 'name', value);
		};

		const handleActiveIngredientCreated = (name: string) => {
			setShowAddActiveIngredientModal(false);
			if (activeIngredientRowIndex !== null) {
				updateIngredient(activeIngredientRowIndex, 'name', name);
				setActiveIngredientRowIndex(null);
			}
		};

		const visibleIngredients = isView
			? form.activeIngredients.filter((i) => i.name || i.concentration)
			: form.activeIngredients;
		const hasComposition = visibleIngredients.length > 0;

		return (
			<>
				{showAddManufacturerModal && (
					<AddManufacturerModal
						isOpen={showAddManufacturerModal}
						onClose={() => setShowAddManufacturerModal(false)}
						onCreated={handleManufacturerCreated}
					/>
				)}
				{showAddCultureModal && (
					<AddCultureModal
						isOpen={showAddCultureModal}
						onClose={() => setShowAddCultureModal(false)}
						onCreated={handleCultureCreated}
					/>
				)}
				{showAddActiveIngredientModal && (
					<AddActiveIngredientModal
						isOpen={showAddActiveIngredientModal}
						onClose={() => {
							setShowAddActiveIngredientModal(false);
							setActiveIngredientRowIndex(null);
						}}
						onCreated={handleActiveIngredientCreated}
					/>
				)}
				<div className={generalStyles.form}>
					<div className={generalStyles.section}>
						<FormSectionTitle>Identificação do Produto</FormSectionTitle>
						<div className={generalStyles.sectionFields}>
							<FormField label="Nome Comercial" required={!isView} error={errors.commercialName}>
								{isView ? (
									<span className={generalStyles.viewText}>{form.commercialName || '—'}</span>
								) : (
									<FormInput
										placeholder="Ex.: Score 250 EC"
										value={form.commercialName}
										onChange={(e) => {
											set('commercialName', e.target.value);
											triggerChange({ ...form, commercialName: e.target.value });
										}}
										hasError={!!errors.commercialName}
									/>
								)}
							</FormField>
							<div className={styles.row2}>
								<FormField
									label="Fabricante / Marca"
									required={!isView}
									error={errors.manufacturerId}
								>
									{isView ? (
										<span className={generalStyles.viewText}>
											{resolveLabel(manufacturerOptions, form.manufacturerId)}
										</span>
									) : (
										<FormSelect
											value={form.manufacturerId}
											onChange={handleManufacturerChange}
											options={manufacturerOptions}
											placeholder="Selecione o fabricante"
											hasError={!!errors.manufacturerId}
										/>
									)}
								</FormField>
								<FormField label="Natureza do Produto" required={!isView} error={errors.nature}>
									{isView ? (
										<span className={generalStyles.viewText}>
											{resolveLabel(NATURE_OPTIONS, form.nature)}
										</span>
									) : (
										<FormSelect
											value={form.nature}
											onChange={(v) => {
												set('nature', v);
												triggerChange({ ...form, nature: v });
											}}
											options={NATURE_OPTIONS}
											placeholder="Selecione a natureza"
											hasError={!!errors.nature}
										/>
									)}
								</FormField>
							</div>
						</div>
					</div>

					{(!isView || hasComposition) && (
						<div className={generalStyles.section}>
							<FormSectionTitle>Composição</FormSectionTitle>
							{isView ? (
								<div className={generalStyles.sectionFields}>
									{visibleIngredients.map(row => (
										<div key={ingredientKeys[form.activeIngredients.indexOf(row)]} className={styles.row2}>
											<FormField label="Ingrediente Ativo">
												<span className={generalStyles.viewText}>{row.name || '—'}</span>
											</FormField>
											<FormField label="Concentração">
												<span className={generalStyles.viewText}>
													{row.concentration ? `${row.concentration} ${concentrationUnit}` : '—'}
												</span>
											</FormField>
										</div>
									))}
								</div>
							) : (
								<div className={styles.dynamicRows}>
									{form.activeIngredients.map((row, i) => (
										<div key={ingredientKeys[i]} className={styles.compositionRow}>
											<div className={styles.compositionIngredient}>
												<FormSelect
													value={row.name}
													onChange={(v) => handleIngredientNameChange(i, v)}
													options={activeIngredientOptions}
													placeholder="Selecione o ingrediente"
													hasError={compositionErrors[i]?.nameError}
												/>
											</div>
											<div className={styles.compositionConcentration}>
												<NutrientInput
													value={row.concentration}
													suffix={concentrationUnit}
													onChange={(e) => updateIngredient(i, 'concentration', e.target.value)}
													hasError={compositionErrors[i]?.concentrationError}
												/>
											</div>
											{i === 0 && <RowActionButton variant="add" title="Adicionar ingrediente" disabled={!row.name || !row.concentration} onClick={addIngredient} />}
											{i > 0 && (
												<RowActionButton variant="delete" onClick={() => removeIngredient(i)} />
											)}
										</div>
									))}
								</div>
							)}
						</div>
					)}

					<div className={generalStyles.section}>
						<FormSectionTitle>Classificação e Aplicação</FormSectionTitle>
						<div className={generalStyles.sectionFields}>
							{isView ? (
								<FormField label="Classes agronômicas">
									<div className={styles.dynamicRows}>
										{classificationRows.filter(row => row.class).map(row => (
											<span key={row.id} className={generalStyles.viewText}>
												{row.class}{row.group ? `, ${row.group}` : ''}
											</span>
										))}
									</div>
								</FormField>
							) : (
								<FormField label="Classes agronômicas e grupos do produto" required error={errors.class}>
									<div className={styles.dynamicRows}>
										{classificationRows.map((row, index) => (
											<div key={row.id} className={styles.compositionRow}>
												<div className={styles.compositionIngredient}>
													<FormSelect
														value={row.class}
														options={[
															...DEFENSIVE_CLASS_OPTIONS,
															...form.class.map(item => item.class).filter(name => !DEFENSIVE_CLASS_OPTIONS.some(option => option.value === name))
																.map(name => ({ value: name, label: name })),
														]}
														placeholder="Selecione a classe agronômica"
														hasError={!!errors.class && !row.class}
														onChange={value => {
															const classes = updateClassificationRow(row.id, 'class', value);
															triggerChange({ ...form, class: classes });
														}}
													/>
												</div>
												<div className={styles.compositionConcentration}>
													<FormInput
														aria-label={`Grupo da linha ${index + 1}`}
														placeholder="Grupo (opcional)"
														value={row.group}
														onChange={event => updateClassificationRow(row.id, 'group', event.target.value)}
													/>
												</div>
												{index === 0 && (
													<RowActionButton variant="add" title="Adicionar classe e grupo" disabled={!row.class} onClick={addClassificationRow} />
												)}
												{index > 0 && (
													<RowActionButton
														variant="delete"
														title={`Remover classe e grupo da linha ${index + 1}`}
														onClick={() => {
															const classes = removeClassificationRow(row.id);
															triggerChange({ ...form, class: classes });
														}}
													/>
												)}
											</div>
										))}
									</div>
								</FormField>
							)}
							<div className={styles.row2}>
								<FormField label="Cultura Recomendada" required={!isView} error={errors.culture}>
									{isView ? (
										<span className={generalStyles.viewText}>
											{form.culture.length > 0 ? form.culture.join(', ') : '—'}
										</span>
									) : (
										<FormSelect
											multiple
											value={form.culture}
											onChange={handleCultureChange}
											options={cultureOptions}
											placeholder="Selecione as culturas"
											hasError={!!errors.culture}
										/>
									)}
								</FormField>
							</div>

							<div className={styles.row2}>
								<FormField
									label="Modo de Aplicação"
									required={!isView}
									error={errors.applicationForm}
								>
									{isView ? (
										<span className={generalStyles.viewText}>
											{resolveApplicationMethodLabels(form.applicationForm)}
										</span>
									) : (
										<FormSelect
											multiple
											value={selectableApplicationForms}
											onChange={setApplicationForm}
											options={APPLICATION_METHOD_OPTIONS}
											placeholder="Selecione a forma"
											hasError={!!errors.applicationForm}
										/>
									)}
								</FormField>
								<FormField label="Dose Base" required={!isView} error={errors.dose}>
									{isView ? (
										<span className={generalStyles.viewText}>
											{form.dose ? `${form.dose} ${form.doseMeasurementUnit || 'L/ha'}` : '—'}
										</span>
									) : (
										<NutrientInput
											value={form.dose}
											suffix={form.doseMeasurementUnit || 'L/ha'}
											placeholder="0.3"
											onChange={(e) => set('dose', e.target.value)}
											hasError={!!errors.dose}
										/>
									)}
								</FormField>
								{!isExclusiveFertigation && (
									<FormField label="Volume Calda">
										{isView ? (
											<span className={generalStyles.viewText}>
												{form.syrupVolume ? `${form.syrupVolume} L/ha` : '—'}
											</span>
										) : (
											<NutrientInput
												value={form.syrupVolume}
												suffix="L/ha"
												placeholder="150"
												onChange={(e) => set('syrupVolume', e.target.value)}
											/>
										)}
									</FormField>
								)}
							</div>
						</div>
					</div>
				</div>
			</>
		);
	}
);
